import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CreditCard,
  ArrowLeft,
  CheckCircle,
  XCircle,
  AlertCircle,
} from 'lucide-react';
import { paymentService } from '../../services/paymentService.js';
import { shipmentService } from '../../services/shipmentService.js';
import Alert from '../common/Alert.js';
import {
  PageLayout,
  Button,
  FormField,
  DetailCard,
} from '../common/index.js';

export default function AddPayment() {
  const navigate = useNavigate();
  const { shipmentId } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetchingShipment, setFetchingShipment] = useState(true);
  const [shipmentDetails, setShipmentDetails] = useState(null);
  const [formData, setFormData] = useState({
    payment_amount: '',
    payment_mode: 'cash',
    remarks: '',
  });

  const paymentModes = [
    { value: 'cash', label: 'CASH' },
    { value: 'upi', label: 'UPI' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'cheque', label: 'Cheque' },
  ];

  useEffect(() => {
    fetchShipmentDetails();
  }, [shipmentId]);

  const fetchShipmentDetails = async () => {
    try {
      setFetchingShipment(true);
      const response = await shipmentService.getShipment(shipmentId);
      const shipment = response.data.shipment || response.data.data;
      setShipmentDetails(shipment);
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to fetch shipment details');
      navigate('/payment-history');
    } finally {
      setFetchingShipment(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'payment_amount') {
      if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const calculateNewBalance = () => {
    const paymentAmount = parseFloat(formData.payment_amount || 0);
    const currentBalance = shipmentDetails?.balance_amount || 0;
    return parseFloat((currentBalance - paymentAmount).toFixed(2));
  };

  const isPaymentAmountExceeded = () => {
    const paymentAmount = parseFloat(formData.payment_amount || 0);
    const currentBalance = shipmentDetails?.balance_amount || 0;
    return parseFloat(paymentAmount.toFixed(2)) > parseFloat(currentBalance.toFixed(2));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const paymentAmount = parseFloat(formData.payment_amount);

    if (isNaN(paymentAmount) || paymentAmount <= 0) {
      Alert.error('Please enter a valid payment amount');
      return;
    }

    if (parseFloat(paymentAmount.toFixed(2)) > parseFloat(shipmentDetails.balance_amount.toFixed(2))) {
      Alert.error(`Payment amount cannot exceed balance (₹${shipmentDetails.balance_amount.toFixed(2)})`);
      return;
    }

    const confirmMessage = `Record payment of ₹${paymentAmount.toFixed(2)} for shipment ${shipmentId}?`;
    const confirmed = await Alert.confirm(confirmMessage, 'Confirm Payment');
    if (!confirmed) return;

    try {
      setLoading(true);
      await paymentService.addPayment(shipmentId, formData);
      Alert.success('Payment recorded successfully');
      navigate(`/shipments/view/${shipmentId}`);
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  if (fetchingShipment) {
    return (
      <PageLayout title="Record Payment" icon={CreditCard}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-sm text-gray-600">Loading shipment details...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  const newBalance = calculateNewBalance();
  const isFullyPaid = shipmentDetails?.balance_amount <= 0;
  const isAmountExceeded = isPaymentAmountExceeded();
  const isSubmitDisabled = loading || isFullyPaid || isAmountExceeded;

  return (
    <PageLayout
      title="Record Payment"
      icon={CreditCard}
      maxWidth="max-w-7xl"
      actions={
        <Button
          variant="outline"
          icon={ArrowLeft}
          onClick={() => navigate(`/shipments/view/${shipmentId}`)}
        >
          Back to Shipment
        </Button>
      }
    >
      {shipmentDetails && (
        <DetailCard title="Shipment Summary" className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Shipment ID</p>
              <p className="font-semibold text-secondary text-lg">#{shipmentDetails.shipment_id}</p>
            </div>

            <div className="p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Total Amount</p>
              <p className="font-semibold text-secondary text-lg">₹{shipmentDetails.total_amount?.toFixed(2)}</p>
            </div>

            <div className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <p className="text-xs font-semibold uppercase tracking-wide">Paid Amount</p>
              </div>
              <p className="font-semibold text-secondary text-lg">₹{shipmentDetails.paid_amount?.toFixed(2)}</p>
            </div>

            <div className="p-4">
              <div className="flex items-center gap-2 mb-1">
                {isFullyPaid ? (
                  <CheckCircle className="w-4 h-4 text-green-600" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-600" />
                )}
                <p
                  className={`text-xs font-medium uppercase tracking-wide ${
                    isFullyPaid ? 'font-semibold text-secondary' : 'text-red-700'
                  }`}
                >
                  {isFullyPaid ? 'Fully Paid' : 'Balance Due'}
                </p>
              </div>
              <p
                className={`font-bold text-lg ${
                  isFullyPaid ? 'font-semibold text-secondary' : 'text-red-700'
                }`}
              >
                ₹{shipmentDetails.balance_amount?.toFixed(2)}
              </p>
            </div>
          </div>

          {isFullyPaid && (
            <div className="mt-4 bg-gradient-to-r from-green-500 to-emerald-500 rounded-sm p-4">
              <div className="flex items-center gap-3 text-white">
                <div className="bg-white/20 p-2 rounded-sm">
                  <CheckCircle className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-bold text-sm">Payment Complete!</p>
                  <p className="text-xs text-green-50">This shipment has been fully paid</p>
                </div>
              </div>
            </div>
          )}
        </DetailCard>
      )}

      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-card shadow-card p-4 sm:p-6 lg:p-8"
      >
        <h3 className="text-lg font-semibold text-secondary mb-4 pb-3 border-b border-gray-100">
          Payment Details
        </h3>

        <div className="space-y-4 sm:space-y-6">
          <FormField label="Payment Amount" required>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <span className="text-gray-500 font-semibold">₹</span>
              </div>
              <input
                type="text"
                name="payment_amount"
                value={formData.payment_amount}
                onChange={handleChange}
                placeholder="0.00"
                required
                disabled={isFullyPaid}
                className={`input-clean pl-9 font-semibold disabled:bg-gray-100 disabled:cursor-not-allowed ${
                  isAmountExceeded && formData.payment_amount ? 'border-danger focus:ring-danger/30' : ''
                }`}
              />
            </div>
            <div className="flex items-center justify-between mt-2 text-xs">
              <span className="text-gray-500">
                Maximum:{' '}
                <span className="font-semibold text-gray-700">
                  ₹{shipmentDetails?.balance_amount?.toFixed(2)}
                </span>
              </span>
              {formData.payment_amount && shipmentDetails && (
                <>
                  {isAmountExceeded ? (
                    <span className="text-red-600 font-semibold bg-red-50 px-3 py-1 rounded-full flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      Amount Exceeds Balance
                    </span>
                  ) : (
                    <span className="text-blue-600 font-semibold bg-blue-50 px-3 py-1 rounded-full">
                      New Balance: ₹{newBalance.toFixed(2)}
                    </span>
                  )}
                </>
              )}
            </div>
          </FormField>

          <FormField label="Payment Mode" required>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <CreditCard className="w-5 h-5 text-gray-400" />
              </div>
              <select
                name="payment_mode"
                value={formData.payment_mode}
                onChange={handleChange}
                required
                disabled={isFullyPaid}
                className="input-clean pl-12 font-semibold disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                {paymentModes.map((mode) => (
                  <option key={mode.value} value={mode.value}>
                    {mode.label}
                  </option>
                ))}
              </select>
            </div>
          </FormField>

          <FormField label="Remarks (Optional)">
            <div className="relative">
              <textarea
                name="remarks"
                value={formData.remarks}
                onChange={handleChange}
                placeholder="Add any additional notes or comments about this payment..."
                rows={4}
                disabled={isFullyPaid}
                className="input-clean resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              <div className="absolute bottom-3 right-3 text-xs text-gray-400">
                {formData.remarks.length} characters
              </div>
            </div>
          </FormField>
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 mt-6 pt-6 border-t border-gray-100">
          <Button
            variant="outline"
            onClick={() => navigate(`/shipments/view/${shipmentId}`)}
            className="sm:w-auto w-full"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            icon={CheckCircle}
            disabled={isSubmitDisabled}
            loading={loading}
            className="sm:w-auto w-full sm:ml-auto"
          >
            {loading ? 'Recording Payment...' : 'Record Payment'}
          </Button>
        </div>
      </form>
    </PageLayout>
  );
}
